"use client";

import {
  archiveAllNotificationsAction,
  archiveNotificationAction,
  deleteAllNotificationsAction,
  deleteNotificationAction,
  markAllNotificationsAsReadAction,
  markNotificationAsReadAction,
} from "@/components/notifications/actions";
import { NotificationItem } from "@/lib/services/notifications/types";
import { useNotificationSocket } from "@/lib/services/socket/unified-socket-service";
import { createContext, useCallback, useContext, useEffect, useState } from "react";

interface NotificationContextType {
  notifications: NotificationItem[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markNotificationAsRead: (id: string) => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;
  archiveAllNotifications: () => Promise<void>;
  archiveNotification: (id: string) => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  deleteAllNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

function isNotificationItem(data: unknown): data is NotificationItem {
  return (
    typeof data === 'object' &&
    data !== null &&
    'id' in data &&
    'read' in data
  );
}

export function NotificationProvider({ children, userId }: {
  children: React.ReactNode;
  userId?: string
}) {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isConnected, getUnreadCount, onNotification, onUnreadCountUpdate } = useNotificationSocket();

  // Add action handlers
  const markNotificationAsRead = useCallback(async (id: string) => {
    try {
      if (userId) {
        const result = await markNotificationAsReadAction(id, userId);
        if (result.success) {
          setNotifications(prev =>
            prev.map(n => n.id === id ? { ...n, read: true } : n)
          );
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      }
    } catch (error) {
      setError('Failed to mark notification as read');
    }
  }, [userId]);

  const markAllNotificationsAsRead = useCallback(async () => {
    try {
      if (userId) {
        const result = await markAllNotificationsAsReadAction(userId);
        if (result.success) {
          setNotifications(prev => prev.map(n => ({ ...n, read: true })));
          setUnreadCount(0);
        }
      }
    } catch (error) {
      setError('Failed to mark all notifications as read');
    }
  }, [userId]);

  const archiveNotification = useCallback(async (id: string) => {
    try {
      if (userId) {
        const result = await archiveNotificationAction(id, userId);
        if (result.success) {
          setNotifications(prev =>
            prev.map(n => n.id === id ? { ...n, archived: true } : n)
          );
          if (!notifications.find(n => n.id === id)?.read) {
            setUnreadCount(prev => Math.max(0, prev - 1));
          }
        }
      }
    } catch (error) {
      setError('Failed to archive notification');
    }
  }, [userId]);

  const archiveAllNotifications = useCallback(async () => {
    try {
      if (userId) {
        const result = await archiveAllNotificationsAction(userId);
        if (result.success) {
          setNotifications(prev => prev.map(n => ({ ...n, archived: true })));
          setUnreadCount(0);
        }
      }
    } catch (error) {
      setError('Failed to archive all notifications');
    }
  }, [userId]);

  const deleteNotification = useCallback(async (id: string) => {
    try {
      if (userId) {
        const result = await deleteNotificationAction(id, userId);
        if (result.success) {
          setNotifications(prev => prev.filter(n => n.id !== id));
          const wasUnread = notifications.find(n => n.id === id && !n.read);
          if (wasUnread) {
            setUnreadCount(prev => Math.max(0, prev - 1));
          }
        }
      }
    } catch (error) {
      setError('Failed to delete notification');
    }
  }, [userId, notifications]);

  const deleteAllNotifications = useCallback(async () => {
    try {
      if (userId) {
        const result = await deleteAllNotificationsAction(userId);
        if (result.success) {
          setNotifications([]);
          setUnreadCount(0);
        }
      }
    } catch (error) {
      setError('Failed to delete all notifications');
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/notifications');
        const result: { success: boolean; notifications: NotificationItem[]; unreadCount: number; error?: string } = await response.json();

        if (result.success) {
          setNotifications(result.notifications);
          setUnreadCount(result.unreadCount);
        } else if (result.error) {
          setError(result.error);
        }
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
        setError(err instanceof Error ? err.message : "Failed to fetch notifications");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Socket connection with error handling
    let notificationCleanup = () => {};
    let countCleanup = () => {};

    try {
      // Only initialize socket in browser environment
      if (typeof window !== 'undefined' && isConnected) {
        // Request initial unread count
        getUnreadCount();

        // Subscribe to notification events
        notificationCleanup = onNotification((data: unknown) => {
          if (isNotificationItem(data)) {
            setNotifications((prev) => [data, ...prev]);
            setUnreadCount((prev) => prev + 1);
          }
        });

        // Subscribe to unread count updates
        countCleanup = onUnreadCountUpdate((count) => {
          setUnreadCount(count as number);
        });

       // console.log('Subscribed to notification events');
      } else {
        console.warn('Socket not connected, notifications will not be real-time');
      }
    } catch (err) {
      console.error('Error setting up notification socket:', err);
      setError('Failed to connect to notification service');
    }

    return () => {
      try {
        notificationCleanup();
        countCleanup();
      } catch (err) {
        console.error('Error cleaning up notification subscriptions:', err);
      }
    };
  }, [userId, isConnected, getUnreadCount, onNotification, onUnreadCountUpdate]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        error,
        markNotificationAsRead,
        markAllNotificationsAsRead,
        archiveNotification,
        archiveAllNotifications,
        deleteNotification,
        deleteAllNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
};
